import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { RoomsViewDialogComponent } from './rooms-view-dialog/rooms-view-dialog.component';
import { GuestsViewDialogComponent } from './guests-view-dialog/guests-view-dialog.component';
import { EstablishmentService } from '../../services/establishment/establishment.service';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../services/permission/permission.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { MatSortModule, Sort } from '@angular/material/sort';
import { sortTableRows } from '../../common-files/table-sort';
import { canCheckInBooking } from '../../common-files/booking-status';

// the branch front desk: "Ready to check in" (confirmed + assigned) and
// "Currently staying" (checked in). One enriched fetch carries the booker's
// name, guests (unmasked aadhaar for identity checks) and rooms/properties;
// the filter bar narrows BOTH lists client-side.
@Component({
  selector: 'app-establishment-checkin-checkout',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatSortModule, LoaderComponent],
  templateUrl: './establishment-checkin-checkout.component.html',
  styleUrl: './establishment-checkin-checkout.component.scss',
})
export class EstablishmentCheckinCheckoutComponent implements OnInit {
  private static readonly PAGE_SIZE = 20;

  private allToCheckIn: any[] = [];
  private allResidents: any[] = [];
  toCheckIn: any[] = [];
  residents: any[] = [];
  activeTab: 'checkin' | 'staying' = 'checkin';
  // infinite scroll: rows render in pages of 20 as the list is scrolled.
  checkInLimit: number = EstablishmentCheckinCheckoutComponent.PAGE_SIZE;
  stayingLimit: number = EstablishmentCheckinCheckoutComponent.PAGE_SIZE;
  isLoading: boolean = false;
  hasLoaded: boolean = false;
  canCheckIn: boolean = false;
  canCheckOut: boolean = false;
  private checkInSortState: Sort = { active: '', direction: '' };
  private residentSortState: Sort = { active: '', direction: '' };

  // filter bar (applies to both lists)
  fUtsav = new FormControl<number | ''>('');
  fProperty = new FormControl<string>('');
  fFamilyCode = new FormControl('');
  fAadhaar = new FormControl('');
  fBookingId = new FormControl('');
  fRoom = new FormControl('');
  utsavList: any[] = [];
  propertyList: any[] = [];

  constructor(
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.canCheckIn = this.permissionService.can('checkin-checkout', 'can_checkin');
    this.canCheckOut = this.permissionService.can('checkin-checkout', 'can_checkout');
    this.loadFilterOptions();
    this.loadLists();
  }

  // options for the utsav / property dropdowns
  loadFilterOptions(): void {
    this.establishmentService.listUtsavs().subscribe({
      next: (res: any) => {
        this.utsavList = Array.isArray(res?.data?.utsavs) ? res.data.utsavs : [];
      },
    });
    this.establishmentService.listProperties(true).subscribe({
      next: (res: any) => {
        this.propertyList = Array.isArray(res?.data?.properties) ? res.data.properties : [];
      },
    });
  }

  get pageDescription(): string {
    if (this.canCheckIn && this.canCheckOut) {
      return 'Check allocated guests in and complete check-out when they leave.';
    }
    if (this.canCheckIn) {
      return 'Check guests in when their booking is allocated.';
    }
    if (this.canCheckOut) {
      return 'Complete check-out for guests when they leave.';
    }
    return 'View bookings ready for arrival and guests currently staying.';
  }

  loadLists(): void {
    this.isLoading = true;
    this.establishmentService.getCheckinCheckoutLists().subscribe({
      next: (res: any) => {
        this.allToCheckIn = res?.data?.readyToCheckIn || [];
        this.allResidents = res?.data?.residents || [];
        this.applyFilters();
        this.hasLoaded = true;
        this.isLoading = false;
      },
      error: (err) => {
        this.allToCheckIn = [];
        this.allResidents = [];
        this.applyFilters();
        this.hasLoaded = true;
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load check-in / check-out lists.');
      },
    });
  }

  applyFilters(): void {
    this.toCheckIn = this.applyCheckInSort(this.allToCheckIn.filter((b) => this.matchesFilters(b)));
    this.residents = this.applyResidentSort(this.allResidents.filter((b) => this.matchesFilters(b)));
    this.checkInLimit = EstablishmentCheckinCheckoutComponent.PAGE_SIZE;
    this.stayingLimit = EstablishmentCheckinCheckoutComponent.PAGE_SIZE;
  }

  setTab(tab: 'checkin' | 'staying'): void {
    this.activeTab = tab;
  }

  get visibleToCheckIn(): any[] {
    return this.toCheckIn.slice(0, this.checkInLimit);
  }

  get visibleResidents(): any[] {
    return this.residents.slice(0, this.stayingLimit);
  }

  loadMore(): void {
    if (this.activeTab === 'checkin') {
      this.checkInLimit += EstablishmentCheckinCheckoutComponent.PAGE_SIZE;
    } else {
      this.stayingLimit += EstablishmentCheckinCheckoutComponent.PAGE_SIZE;
    }
  }

  // infinite scroll: pull the next page when the list is scrolled near its end.
  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      const hasMore =
        this.activeTab === 'checkin'
          ? this.checkInLimit < this.toCheckIn.length
          : this.stayingLimit < this.residents.length;
      if (hasMore) this.loadMore();
    }
  }

  resetFilters(): void {
    this.fUtsav.setValue('');
    this.fProperty.setValue('');
    this.fFamilyCode.setValue('');
    this.fAadhaar.setValue('');
    this.fBookingId.setValue('');
    this.fRoom.setValue('');
    this.applyFilters();
  }

  private matchesFilters(row: any): boolean {
    const utsavId = this.fUtsav.value;
    const propertyName = (this.fProperty.value || '').trim();
    const familyCode = (this.fFamilyCode.value || '').replace(/\D/g, '');
    const aadhaar = (this.fAadhaar.value || '').replace(/\D/g, '');
    const bookingId = (this.fBookingId.value || '').trim();
    const room = (this.fRoom.value || '').trim().toLowerCase();

    if (utsavId !== '' && utsavId !== null && Number(row.utsavId) !== Number(utsavId)) return false;
    if (propertyName) {
      const names = String(row.properties || '').split(', ');
      if (!names.includes(propertyName)) return false;
    }
    if (familyCode && !String(row.familyCode || '').includes(familyCode)) return false;
    if (bookingId && !String(row.bookingId || '').includes(bookingId)) return false;
    if (room && !String(row.rooms || '').toLowerCase().includes(room)) return false;
    if (aadhaar) {
      const guests = row.guests || [];
      const hit = guests.some((g: any) =>
        String(g.aadhaarNumber || '').replace(/\D/g, '').includes(aadhaar)
      );
      if (!hit) return false;
    }
    return true;
  }

  // open the guests of one booking in its own popup (house dialog style).
  viewGuests(row: any): void {
    this.dialog.open(GuestsViewDialogComponent, {
      width: '620px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        bookingId: row.bookingId,
        familyCode: row.familyCode,
        guests: row.guests || [],
      },
    });
  }

  // open the premises/rooms of one booking in its own popup (house dialog style).
  viewRooms(row: any): void {
    this.dialog.open(RoomsViewDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        bookingId: row.bookingId,
        familyCode: row.familyCode,
        pairs: row.roomAllocations || [],
      },
    });
  }

  // short cell text: "2 rooms · 2 premises"; the pairs open in the popup.
  roomsSummary(row: any): string {
    const pairs = row?.roomAllocations || [];
    if (!pairs.length) return '';
    const premisesCount = new Set(pairs.map((p: any) => p.propertyName)).size;
    const roomsPart = pairs.length === 1 ? '1 room' : `${pairs.length} rooms`;
    const premisesPart = premisesCount === 1 ? '1 premises' : `${premisesCount} premises`;
    return `${roomsPart} · ${premisesPart}`;
  }

  sortCheckIns(sort: Sort): void {
    this.checkInSortState = sort;
    this.toCheckIn = this.applyCheckInSort(this.toCheckIn);
  }

  sortResidents(sort: Sort): void {
    this.residentSortState = sort;
    this.residents = this.applyResidentSort(this.residents);
  }

  private applyCheckInSort(bookings: any[]): any[] {
    return sortTableRows(bookings, this.checkInSortState, {
      bookingId: (booking) => Number(booking.bookingId),
      guestCount: (booking) => Number(booking.guestCount),
    });
  }

  private applyResidentSort(residents: any[]): any[] {
    return sortTableRows(residents, this.residentSortState, {
      bookingId: (resident) => Number(resident.bookingId),
      guestCount: (resident) => Number(resident.guestCount),
    });
  }

  checkIn(booking: any): void {
    if (!this.canCheckIn || !canCheckInBooking(booking)) return;

    this.isLoading = true;
    this.establishmentService.checkIn(Number(booking.bookingId)).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Checked in.');
        this.loadLists();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Check-in failed.');
      },
    });
  }

  checkOut(bookingId: number): void {
    if (!this.canCheckOut) return;

    this.isLoading = true;
    this.establishmentService.checkOut(Number(bookingId)).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Checked out.');
        this.loadLists();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Check-out failed.');
      },
    });
  }
}
